import {
    ArrowUpTrayIcon,
    CheckIcon,
    ClockIcon,
    ListBulletIcon,
    MagnifyingGlassIcon,
    Squares2X2Icon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import React from 'react';
import { FilesTooltip, useFilesConfig } from './config';
import type { LayoutMode, ListMode } from './types';

interface FileToolbarProps {
    searchTerm: string;
    onSearchChange: (term: string) => void;
    selectionMode: boolean;
    onToggleSelectionMode: () => void;
    showUploadZone: boolean;
    onToggleUploadZone: () => void;
    layoutMode: LayoutMode;
    onLayoutModeChange: (mode: LayoutMode) => void;
    listMode: ListMode;
    onListModeChange: (mode: ListMode) => void;
    filesCountText: string;
}

const FileToolbar: React.FC<FileToolbarProps> = ({
    searchTerm,
    onSearchChange,
    selectionMode,
    onToggleSelectionMode,
    showUploadZone,
    onToggleUploadZone,
    layoutMode,
    onLayoutModeChange,
    listMode,
    onListModeChange,
    filesCountText
}) => {
    const { canUpload } = useFilesConfig();

    return (
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-gray-100 flex-shrink-0">
            {/* Search — flex-1 so it shrinks first in narrow containers, but
                capped at w-52 (13rem) so wide pages keep a fixed-width search box. */}
            <div className="relative flex-1 min-w-[120px] max-w-[13rem]">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search files..."
                    className="w-full pl-8 pr-8 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-xs text-gray-700 bg-gray-50/50"
                />
                {searchTerm && (
                    <button
                        onClick={() => onSearchChange('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Clear search"
                    >
                        <XMarkIcon className="w-3 h-3" />
                    </button>
                )}
            </div>
            <button
                onClick={onToggleSelectionMode}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${selectionMode ? 'bg-gray-900 text-white' : 'text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
                <CheckIcon className="w-3 h-3" />
                {selectionMode ? 'Cancel' : 'Select'}
            </button>
            {canUpload && (
                <button
                    onClick={onToggleUploadZone}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${showUploadZone ? 'bg-gray-900 text-white' : 'text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                >
                    <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                    Upload
                </button>
            )}
            {/* View-mode toggles pushed to the right with ml-auto on the first one */}
            <div className="flex-shrink-0 ml-auto flex items-center gap-1.5">
                <div className="inline-flex rounded-md overflow-hidden border border-gray-200">
                    <FilesTooltip content="List view">
                        <button
                            onClick={() => onLayoutModeChange('list')}
                            className={`inline-flex items-center p-1.5 transition-colors ${layoutMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                        >
                            <ListBulletIcon className="w-3.5 h-3.5" />
                        </button>
                    </FilesTooltip>
                    <FilesTooltip content="Grid view">
                        <button
                            onClick={() => onLayoutModeChange('grid')}
                            className={`inline-flex items-center p-1.5 transition-colors border-l border-gray-200 ${layoutMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                        >
                            <Squares2X2Icon className="w-3.5 h-3.5" />
                        </button>
                    </FilesTooltip>
                </div>
                <div className="inline-flex rounded-md overflow-hidden border border-gray-200">
                    <button
                        onClick={() => onListModeChange('category')}
                        className={`inline-flex items-center px-2.5 py-1.5 text-[11px] font-medium transition-colors ${listMode === 'category' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    >
                        Category
                    </button>
                    <button
                        onClick={() => onListModeChange('date')}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-colors border-l border-gray-200 ${listMode === 'date' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    >
                        <ClockIcon className="w-3 h-3" />
                        Date
                    </button>
                </div>
            </div>
            {searchTerm && (
                <span className="w-full text-[11px] text-gray-400">{filesCountText}</span>
            )}
        </div>
    );
};

export { FileToolbar };
